import React from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Box, Button, Grid, IconButton, Typography, styled } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';

import { EGAEventName, useGAHelpers } from 'lib/hooks/useGAHelper';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { isDefined } from 'lib/typeguards';
import { Option } from 'lib/types';
import { ExpandedMatchCodesInputModal } from 'components/adjustment-schemes/modals/ExpandedMatchCodesInputModal';
import { AwardTemplate } from 'components/customers/models/Award';
import {
    getChildItemRuleOptions,
    getPerkDiscountScopeOptions,
    showChildItemRulePerkForm
} from 'components/perks/helpers';
import { AwardRedemptionFullFormData } from './PerkRedemptionForm';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';

const StyledSelectFormField = styled(SelectFormField)(() => ({
    width: 'auto'
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.spacing(2),
    fontWeight: theme.typography.fontWeightMedium
}));

const StyledMatchCodeBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'noWrap'
})<{ noWrap?: boolean }>(({ noWrap }) => ({
    flexDirection: 'row',
    display: 'flex',
    flexWrap: noWrap ? 'nowrap' : 'wrap',
    lineHeight: 2,
    alignItems: 'center',
    gap: '8px'
}));

const perkTypeToLabelOptions: Record<AwardTemplate | string, string> = {
    [AwardTemplate.AD_HOC]: 'Adhoc',
    [AwardTemplate.PSEUDO_CURRENCY]: 'Pseudo Currency',
    [AwardTemplate.STAMP_CARD]: 'Stamp Card',
    [AwardTemplate.CLUB]: 'Audience',
    [AwardTemplate.LEVEL]: 'Level'
};

const scopeOptionsLabels = {
    ITEM: 'Menu Items',
    GROUP: 'Menu Groups',
    CHILD_ITEM: 'Modifier Items',
    ITEM_AND_CHILD_ITEM: 'Menu and Modifier Items',
    BASKET: 'Basket',
    QUALIFIER: 'Qualifier',
    MENU_ITEM: 'Menu Items'
};

const matchCodeLabels = {
    ITEM: 'menu items',
    GROUP: 'menu groups',
    CHILD_ITEM: 'modifier items',
    ITEM_AND_CHILD_ITEM: 'menu and modifier items',
    BASKET: 'basket',
    QUALIFIER: 'qualifier',
    MENU_ITEM: 'menu items'
};

const rules = [
    {
        value: 'CHEAPEST',
        label: 'cheapest'
    },
    {
        value: 'COSTLIEST',
        label: 'costliest'
    },
    {
        value: 'CHEAPEST_FIRST',
        label: 'cheapest first'
    },
    {
        value: 'COSTLIEST_FIRST',
        label: 'costliest first'
    },
    {
        value: 'ALL',
        label: 'all'
    },
    {
        value: 'ALL_ALLOCATE',
        label: 'all (allocate)'
    }
];

const discountChargesOptions: Option[] = [
    { value: '', label: 'also be applied to service charges', description: 'Default' },
    { value: '0', label: 'not be applied to any charges' },
    { value: '1', label: 'also be applied to service charges' }
];

interface PerkDiscountSectionProps {
    currencySymbol?: string;
    perkType: string;
    posProvider?: string;
    isPseudoCurrency: boolean;
}

export const PerkDiscountSection: React.FC<PerkDiscountSectionProps> = ({
    currencySymbol,
    perkType,
    posProvider,
    isPseudoCurrency
}) => {
    const { values, setFieldValue, errors } = useFormikContext<AwardRedemptionFullFormData>();
    const { logUserEvent } = useGAHelpers();
    const [selectedMatchCodeGroupIndex, setSelectedMatchCodeGroupIndex] = React.useState<number>(-1);

    const selectedDiscountMatchCodeGroup = React.useMemo(() => {
        if (selectedMatchCodeGroupIndex === -1) {
            return undefined;
        }
        return values.discount?.groupedScopeMatchCodes[selectedMatchCodeGroupIndex];
    }, [values.discount?.groupedScopeMatchCodes, selectedMatchCodeGroupIndex]);

    const types: Option[] = React.useMemo(
        () =>
            perkType !== AwardTemplate.PSEUDO_CURRENCY && perkType !== AwardTemplate.LEVEL
                ? [
                      {
                          value: 'VALUE',
                          label: currencySymbol || 'Value'
                      },
                      {
                          value: 'PERCENTAGE',
                          label: '%'
                      },
                      {
                          value: 'OVERRIDE',
                          label: 'price override'
                      }
                  ]
                : [
                      {
                          value: 'VALUE',
                          label: currencySymbol || 'Value'
                      },
                      {
                          value: 'PERCENTAGE',
                          label: '%'
                      }
                  ],
        [currencySymbol, perkType]
    );

    const discountRules = React.useMemo(() => {
        switch (perkType) {
            case 'AD_HOC':
            case 'STAMP_CARD':
            case 'CLUB':
                return rules.filter(
                    rule => rule.value !== 'COSTLIEST_FIRST' && rule.value !== 'CHEAPEST_FIRST'
                );
            default:
                return rules;
        }
    }, [perkType]);

    const discountRulesPerDiscountType = React.useMemo(
        () =>
            values.discount?.type === 'PERCENTAGE'
                ? discountRules.filter(rule => rule.value !== 'ALL_ALLOCATE')
                : discountRules,
        [discountRules, values.discount?.type]
    );

    const isChildItemRuleSupported = React.useMemo(
        () => showChildItemRulePerkForm(posProvider),
        [posProvider]
    );

    const showGroupedScopeMatchCodes = React.useMemo(
        () =>
            values.discount?.scope !== 'BASKET' &&
            (values.discount?.childItemRule ||
                (isDefined(values.discount?.rule) && !isChildItemRuleSupported)),
        [
            isChildItemRuleSupported,
            values.discount?.childItemRule,
            values.discount?.rule,
            values.discount?.scope
        ]
    );

    const showMaxCountField = React.useMemo(
        () =>
            (perkType === AwardTemplate.AD_HOC || perkType === AwardTemplate.CLUB) &&
            isDefined(values.discount?.scope) &&
            values.discount?.scope !== 'BASKET' &&
            isDefined(values.discount?.rule) &&
            (values.discount?.rule === 'CHEAPEST' || values.discount?.rule === 'COSTLIEST') &&
            values.discount?.groupedScopeMatchCodes?.length > 0 &&
            values?.discount?.groupedScopeMatchCodes[0].length > 0,
        [perkType, values.discount?.rule, values.discount?.scope, values.discount?.groupedScopeMatchCodes]
    );

    const showAllowQualifierDiscountField = React.useMemo(() => {
        if (perkType === AwardTemplate.PSEUDO_CURRENCY) {
            return false;
        }
        if (isDefined(values.discount?.scope) && values.discount?.scope === 'BASKET') {
            return true;
        }
        return (
            isDefined(values.discount?.rule) &&
            isDefined(values.discount?.type) &&
            isDefined(values.discount?.value) &&
            isDefined(values.discount?.groupedScopeMatchCodes) &&
            isDefined(values.discount?.groupedScopeMatchCodes[0]) &&
            values.discount?.groupedScopeMatchCodes[0].length > 0
        );
    }, [
        values.discount?.scope,
        values.discount?.type,
        values.discount?.value,
        values.discount?.rule,
        values.discount?.groupedScopeMatchCodes,
        perkType
    ]);

    const showDiscountRuleField = React.useMemo(
        () =>
            isDefined(values.discount?.scope) &&
            isDefined(values.discount?.type) &&
            isDefined(values.discount?.value) &&
            values.discount?.scope !== 'BASKET',
        [values.discount?.scope, values.discount?.type, values.discount?.value]
    );

    const showChildDiscountRuleField = React.useMemo(
        () =>
            isDefined(values.discount?.rule) &&
            values.discount?.scope !== 'BASKET' &&
            isChildItemRuleSupported,
        [values.discount?.rule, values.discount?.scope, isChildItemRuleSupported]
    );

    const discountScopeOptions = React.useMemo(() => getPerkDiscountScopeOptions(posProvider), [posProvider]);

    const handleAddCondition = React.useCallback(
        (push: (value: string) => void) => () => {
            logUserEvent(EGAEventName.ScopeMatchCodeAddConditionClick);
            push('');
        },
        [logUserEvent]
    );

    const handleDiscountExpandInput = React.useCallback((index: number) => {
        setSelectedMatchCodeGroupIndex(index);
    }, []);

    const handleConfirmDiscountMatchCodeEdit = React.useCallback(
        (editedMatchCodes: string) => {
            const currentValues = values.discount?.groupedScopeMatchCodes;
            if (currentValues && selectedMatchCodeGroupIndex > -1) {
                currentValues[selectedMatchCodeGroupIndex] = editedMatchCodes;
                setFieldValue('discount.groupedScopeMatchCodes', [...currentValues]);
            }
            setSelectedMatchCodeGroupIndex(-1);
        },
        [setFieldValue, selectedMatchCodeGroupIndex, values.discount?.groupedScopeMatchCodes]
    );

    const handleCloseDiscountExpandedInput = React.useCallback(() => {
        setSelectedMatchCodeGroupIndex(-1);
    }, []);

    const handleDiscountScopeSelect = React.useCallback(
        (value: any) => {
            if (value === 'CHILD_ITEM' || value === 'ITEM_AND_CHILD_ITEM') {
                setFieldValue('discount.childItemRule', 'EXCLUDE');
            } else {
                setFieldValue('discount.childItemRule', undefined);
            }
        },
        [setFieldValue]
    );

    return (
        <Grid size={12}>
            <Box display="flex" flexDirection="column" marginBottom={1.5}>
                <StyledTitle>4.1 Setting up the discount</StyledTitle>
                <Typography variant="caption">
                    This section controls the discountable items. It determines based on the rules that are
                    supplied to it, what items should be discounted and by how much
                </Typography>
            </Box>
            <Grid container spacing={1.5}>
                <Grid size={12}>
                    <Box display="flex" alignItems="center" columnGap="4px">
                        <Typography variant="body2">
                            This{' '}
                            {perkType in perkTypeToLabelOptions
                                ? perkTypeToLabelOptions[perkType].toLowerCase()
                                : 'Unknown'}{' '}
                            perk applies a
                        </Typography>
                        <Box width={values.discount?.type === 'OVERRIDE' ? 130 : 40}>
                            <SelectFormField
                                options={types}
                                name="discount.type"
                                disabled={isPseudoCurrency}
                            />
                        </Box>
                        {values.discount?.type === 'OVERRIDE' && (
                            <Typography variant="body2">of {currencySymbol ?? ''}</Typography>
                        )}
                        <Box width={44}>
                            <TextFormField name="discount.value" type="number" disabled={isPseudoCurrency} />
                        </Box>
                        <Typography variant="body2">
                            {values.discount?.type === 'OVERRIDE' ? 'to' : 'discount to'}
                        </Typography>
                        <Box>
                            <StyledSelectFormField
                                options={discountScopeOptions}
                                name="discount.scope"
                                onSelect={handleDiscountScopeSelect}
                            />
                        </Box>
                    </Box>
                </Grid>
                {showDiscountRuleField && (
                    <Grid size={12}>
                        <Box display="flex" alignItems="center" columnGap="4px">
                            <Typography variant="body2">
                                {`${
                                    values.discount.type === 'PERCENTAGE'
                                        ? `${values.discount.value}%`
                                        : `${currencySymbol}${values.discount.value}`
                                }`}{' '}
                                off is redeemed on
                            </Typography>
                            <Box>
                                <StyledSelectFormField
                                    options={discountRulesPerDiscountType}
                                    name="discount.rule"
                                />
                            </Box>
                            <Typography variant="body2">product(s) in the basket</Typography>
                        </Box>
                    </Grid>
                )}
                {showChildDiscountRuleField && (
                    <Grid size={12}>
                        <Box display="flex" alignItems="center" columnGap="4px">
                            <Typography variant="body2">The cost of modifier products are</Typography>
                            <Box>
                                <StyledSelectFormField
                                    options={getChildItemRuleOptions(posProvider, values.discount?.scope)}
                                    name="discount.childItemRule"
                                    disabled={
                                        values.discount?.scope === 'CHILD_ITEM' ||
                                        values.discount?.scope === 'ITEM_AND_CHILD_ITEM'
                                    }
                                />
                            </Box>
                            <Typography variant="body2">when calculating the discount</Typography>
                        </Box>
                    </Grid>
                )}
                {showGroupedScopeMatchCodes && (
                    <Grid size={12}>
                        <FieldArray name="discount.groupedScopeMatchCodes">
                            {({ remove, push }) => (
                                <StyledMatchCodeBox>
                                    <Typography variant="body2" lineHeight={2}>
                                        Perk{' '}
                                        {values.discount?.scope &&
                                            matchCodeLabels[
                                                values.discount.scope as keyof typeof matchCodeLabels
                                            ]}{' '}
                                        are
                                    </Typography>
                                    {values.discount?.groupedScopeMatchCodes?.map((_, index, array) => (
                                        <StyledMatchCodeBox noWrap key={index}>
                                            <TextFormField
                                                name={`discount.groupedScopeMatchCodes.${index}`}
                                                placeholder={'e.g. free drink'}
                                                maxRows={4}
                                            />
                                            {
                                                <IconButton
                                                    size="small"
                                                    // eslint-disable-next-line react/jsx-no-bind
                                                    onClick={() => handleDiscountExpandInput(index)}
                                                >
                                                    <OpenInFullIcon />
                                                </IconButton>
                                            }
                                            {index > 0 && (
                                                <IconButton
                                                    size="small"
                                                    // eslint-disable-next-line react/jsx-no-bind
                                                    onClick={() => remove(index)}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            )}
                                            {index >= 0 && array.length - 1 > index && (
                                                <Typography variant="body2">and</Typography>
                                            )}
                                        </StyledMatchCodeBox>
                                    ))}
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleAddCondition(push)}
                                    >
                                        Add another condition
                                    </Button>
                                </StyledMatchCodeBox>
                            )}
                        </FieldArray>
                        <Typography variant="body2" fontStyle="italic" lineHeight={2}>
                            If you have entered a match code, make sure you assign the match code to the{' '}
                            {values.discount?.scope &&
                                scopeOptionsLabels[
                                    values.discount.scope as keyof typeof scopeOptionsLabels
                                ]}{' '}
                            in your menu via the menu manager page under the Perks column in the table
                        </Typography>
                    </Grid>
                )}
                {showMaxCountField && (
                    <Grid size={12}>
                        <Box display="flex" alignItems="center" columnGap="4px">
                            <Typography variant="body2">The perk can be applied</Typography>
                            <Box width={44}>
                                <TextFormField
                                    name="discount.maxCount"
                                    type="number"
                                    showHelperText={false}
                                />
                            </Box>
                            <Typography variant="body2">
                                time(s) in a single basket for each matching qualifier (if a qualifier has
                                been set)
                            </Typography>
                        </Box>
                        {errors.discount?.maxCount && (
                            <Typography variant="body2" color="error">
                                {errors.discount?.maxCount}
                            </Typography>
                        )}
                    </Grid>
                )}
                {showAllowQualifierDiscountField && (
                    <Grid size={12}>
                        <SwitchFormField
                            name="discount.allowQualifierDiscount"
                            label={<MuiFormLabel>Allow qualifier items to be discounted</MuiFormLabel>}
                        />
                    </Grid>
                )}
                <Grid size={12}>
                    <Box display="flex" alignItems="center" columnGap="4px">
                        <Typography variant="body2">The discount will</Typography>
                        <Box>
                            <StyledSelectFormField
                                options={discountChargesOptions}
                                name="discount.discountCharges"
                                displayEmpty
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            {selectedDiscountMatchCodeGroup !== undefined && (
                <ExpandedMatchCodesInputModal
                    open={selectedMatchCodeGroupIndex > -1}
                    matchCodes={selectedDiscountMatchCodeGroup}
                    onConfirm={handleConfirmDiscountMatchCodeEdit}
                    onClose={handleCloseDiscountExpandedInput}
                />
            )}
        </Grid>
    );
};
